import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { getDeviceToken } from '../pages/customer/feed/LikeShareBar';

export type PublicationType = 'ARRIVAGE' | 'PROMOTION' | 'ANTI_GASPI' | 'EVENEMENT' | 'INFO';
export type PublicationStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'EXPIRED' | 'ARCHIVED';

export interface Publication {
  id: string;
  tenant_id: string;
  type: PublicationType;
  status: PublicationStatus;
  title: string;
  content: string;
  summary?: string;
  images?: Array<{
    id: string;
    url: string;
    caption?: string;
    display_order: number;
  }>;
  videos?: Array<{
    id: string;
    url: string;
    thumbnail_url?: string;
    caption?: string;
    display_order: number;
  }>;
  products?: Array<{
    id: string;
    product_id: string;
    highlight_price: boolean;
    special_offer?: string;
    /**
     * Ce que le serveur sert d'un produit attaché : un `select` explicite, pas
     * la ligne entière (prix d'achat et fournisseur sont sur la même table, et
     * cette route est publique). Typé, pour que le prochain champ affiché soit
     * un champ qu'on a décidé de servir.
     */
    product: {
      id: string;
      name: string;
      description?: string | null;
      unit?: string | null;
      is_weighted?: boolean;
      vegan?: boolean;
      active?: boolean;
      /** Champ LEGACY, vide sur tout ce qui a été photographié depuis mars 2026. */
      image_url?: string | null;
      images?: Array<{
        url: string;
        thumbnail_url?: string | null;
        position?: number;
        type?: string;
      }>;
      prices?: Array<{ amount_cents: number; currency?: string }>;
    };
  }>;
  views_count: number;
  clicks_count: number;
  shares_count: number;
  likes_count?: number;
  /** cet appareil a aimé (calculé par appareil, hors cache) */
  liked?: boolean;
  published_at?: string;
  expires_at?: string;
  /** Envoyée par email aux abonnés (au plus une fois) */
  emailed_at?: string | null;
  created_at: string;
  updated_at: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    city?: string;
    address?: string;
  };
}

export interface PublicationsFilters {
  type?: Publication['type'];
  status?: Publication['status'];
  page?: number;
  limit?: number;
}

export interface UsePublicationsReturn {
  publications: Publication[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  totalPages: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (data: CreatePublicationData) => Promise<Publication>;
  update: (id: string, data: Partial<CreatePublicationData>) => Promise<Publication>;
  publish: (id: string) => Promise<Publication>;
  archive: (id: string) => Promise<Publication>;
  remove: (id: string) => Promise<void>;
  trackView: (publicationId: string, data?: Partial<TrackViewData>) => Promise<void>;
}

export interface CreatePublicationData {
  type: Publication['type'];
  title: string;
  content: string;
  summary?: string;
  images?: Array<{
    url: string;
    caption?: string;
    display_order?: number;
  }>;
  videos?: Array<{
    url: string;
    thumbnail_url?: string;
    caption?: string;
    display_order?: number;
  }>;
  products?: Array<{
    product_id: string;
    highlight_price?: boolean;
    special_offer?: string;
    display_order?: number;
  }>;
  scheduled_for?: string;
  expires_at?: string;
}

export interface TrackViewData {
  customer_id?: string;
  view_duration_seconds?: number;
  clicked_product?: boolean;
}

export const usePublications = (
  filters: PublicationsFilters = {},
  tenantId?: string
): UsePublicationsReturn => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(filters.page || 1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(0);

  const fetchPublications = useCallback(async (currentPage: number, append: boolean = false) => {
    if (loading) return;

    // Sans tenant connu (ni en paramètre, ni mémorisé par le client API), la
    // requête partirait sans en-tête et reviendrait en 400 : on attend que le
    // contexte le fournisse — l'effet ci-dessous relance dès qu'il change.
    if (!tenantId && !localStorage.getItem('customer_tenant_id') && !localStorage.getItem('tenant')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: currentPage,
        limit: filters.limit || 10,
      };

      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      params.device_token = getDeviceToken();

      const responseData = await api.get('/publications', {
        params,
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });

      // Gérer les deux formats de réponse possibles
      const isArrayResponse = Array.isArray(responseData);
      const data = isArrayResponse ? responseData : responseData.data;
      const meta = isArrayResponse ? { page: currentPage, totalPages: 1 } : responseData.meta;

      if (append) {
        setPublications((prev) => [...prev, ...data]);
      } else {
        setPublications(data);
      }

      setHasMore(meta && meta.page < meta.totalPages);
      setTotalPages(meta?.totalPages || 1);
      setPage(currentPage);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des publications');
      console.error('Error fetching publications:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, tenantId, loading]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchPublications(page + 1, true);
  }, [page, hasMore, loading, fetchPublications]);

  const refresh = useCallback(async () => {
    setPublications([]);
    setPage(1);
    await fetchPublications(1, false);
  }, [fetchPublications]);

  const create = useCallback(async (data: CreatePublicationData): Promise<Publication> => {
    try {
      const publication = await api.post('/publications', data, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      await refresh();
      return publication;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erreur lors de la création');
    }
  }, [tenantId, refresh]);

  const update = useCallback(async (id: string, data: Partial<CreatePublicationData>): Promise<Publication> => {
    try {
      const publication = await api.put(`/publications/${id}`, data, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      await refresh();
      return publication;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  }, [tenantId, refresh]);

  const publish = useCallback(async (id: string): Promise<Publication> => {
    try {
      const publication = await api.post(`/publications/${id}/publish`, {}, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      await refresh();
      return publication;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erreur lors de la publication');
    }
  }, [tenantId, refresh]);

  const archive = useCallback(async (id: string): Promise<Publication> => {
    try {
      const publication = await api.post(`/publications/${id}/archive`, {}, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      await refresh();
      return publication;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erreur lors de l\'archivage');
    }
  }, [tenantId, refresh]);

  const remove = useCallback(async (id: string): Promise<void> => {
    try {
      await api.delete(`/publications/${id}`, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      await refresh();
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  }, [tenantId, refresh]);

  const trackView = useCallback(async (publicationId: string, data: Partial<TrackViewData> = {}): Promise<void> => {
    try {
      await api.post('/publications/track-view', {
        publication_id: publicationId,
        ...data,
      }, {
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
    } catch (err: any) {
      console.error('Error tracking view:', err);
      // Ne pas bloquer l'UI si le tracking échoue
    }
  }, [tenantId]);

  // `tenantId` fait partie des dépendances : sur la boutique, le premier rendu
  // part souvent AVANT que le tenant soit résolu (requête sans en-tête → 400) ;
  // sans ce déclencheur, le fil restait vide jusqu'au prochain rechargement.
  useEffect(() => {
    fetchPublications(1, false);
  }, [filters.type, filters.status, filters.limit, tenantId]);

  return {
    publications,
    loading,
    error,
    hasMore,
    page,
    totalPages,
    loadMore,
    refresh,
    create,
    update,
    publish,
    archive,
    remove,
    trackView,
  };
};
